/*===================================================================
=       USE SPENDING DEMAND CORE                   =
====================================================================*/
/**
 *
 *
 */

import { AxiosError } from "axios";
import { handlerErrorFunctions } from "../../../../utils/errorHlandlerUtils";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SpendingDemandAPI from "../../services/SpendingDemandAPI";
import {
  FilterSpendingDemand,
  SpendingDemand,
  SpendingDemandExtended,
  ExtractField,
  DEFAULT_EXTRACT_FIELDS,
} from "../../types/spendingDemand.types";


import useSpendingDemandFilter from "../useSpendingDemandFilter";
import { useDispatch, useSelector } from "react-redux";
import { spendingDemandMapState } from "../../redux/spendingDemand.mapstate";
import {
  setSelectModeAction,
  setSpendingDemandSelectedListAction,
} from "../../redux/spendingDmeand.actions";
import { Spending } from "../../../spending/types/spending.types";
import { FileUrl } from "../../../../components/molecules/FileListManager/FileListManager";
import { FilterpendingDemandConverterToQuery } from "../../utils/filterConverter";

export type Action = "VALIDATE" | "REJECT" | "REQUEST_CORRECTION" | "CREATE_CREDIT";
export interface SpendingDemandSettings {
  handleNetworkErrorFunction:
    | ((error: AxiosError<unknown, any>, SreenContext: string | null) => void)
    | null;
  handleInternErrorFunction:
    | ((errorMessage: string | null, errorCode: number | null) => void)
    | null;
  getCurrentPage: (() => number | null) | null;

  getCurrentFilter: () => FilterSpendingDemand;

  employeeId: string;
  userId: string;
  forUser?: boolean;
  disableQuery?: boolean;
}

export const useSpendingDemandCore = ({
  handleNetworkErrorFunction,
  handleInternErrorFunction,
  getCurrentPage,
  getCurrentFilter,
  employeeId,
  userId,
  forUser = false,
  disableQuery = false
}: SpendingDemandSettings) => {
  const { handleNetworkError, handleInternError } = handlerErrorFunctions(
    handleNetworkErrorFunction,
    handleInternErrorFunction
  );
  const dispatch = useDispatch();
  const {
    spendingDemand: { spendingDemandSelected, isSelectMode },
  } = useSelector(spendingDemandMapState);
  const currentFilter = getCurrentFilter();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<FilterSpendingDemand | null>(
    currentFilter
  );
  const currentPage = getCurrentPage ? getCurrentPage() : null;
  const [isLoadingActionGrouped, setIsLoadingActionGrouped] = useState(false);
  const [groupedActionsProgress, setGroupedActionsProgress] = useState<{
    current: number;
    total: number;
    completed: number;
    failed: number;
  }>({ current: 0, total: 0, completed: 0, failed: 0 });
  const [groupedActionsMessage, setGroupedActionsMessage] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Extract states
  const [extractProcessId, setExtractProcessId] = useState<string | null>(null);
  const [extractStatus, setExtractStatus] = useState<{
    status: 'PENDING' | 'COMPLETED' | 'ERROR' | 'NOT_FOUND' | null;
    total: number;
    actual: number;
  }>({ status: null, total: 0, actual: 0 });
  const [isExtracting, setIsExtracting] = useState(false);
  const [selectedExtractFields, setSelectedExtractFields] = useState<ExtractField[]>(DEFAULT_EXTRACT_FIELDS);

  const { filter: filterV2 } = useSpendingDemandFilter();
  const queryClient = useQueryClient();

 

  useEffect(() => {
    if (currentPage) {
      setPage(currentPage);
    }
    setFilter(filterV2);
  }, [currentPage, currentFilter, filterV2]);

  const allSpendingDemandListQuery = useQuery({
    enabled: !disableQuery,
    queryFn: () =>
      SpendingDemandAPI.getFetchAllSpendingDemand(
        10,
        page,
        filter,
        userId,
        forUser
      ),
    queryKey: ["spendingdemand", { page, filter }],
  });

  useEffect(() => {
    if (allSpendingDemandListQuery.isError) {
      handleNetworkError(
        allSpendingDemandListQuery.error as AxiosError,
        "spendingDemandList"
      );
    }
  }, [allSpendingDemandListQuery.isError]); //eslint-disable-line

  const setSpendingDemandSelectedList = (
    spendingDemandSelected: SpendingDemand[] | null
  ) => {
    dispatch(setSpendingDemandSelectedListAction(spendingDemandSelected));
  };

  const processGroupedActionsSequentially = async (
    spendingDemandSelected: SpendingDemand[] | null,
    action: Action,
    message?: string
  ) => {
    if (!spendingDemandSelected || spendingDemandSelected.length === 0) return;

    setIsLoadingActionGrouped(true);
    setGroupedActionsProgress({ current: 0, total: spendingDemandSelected.length, completed: 0, failed: 0 });
    setGroupedActionsMessage({ type: null, message: '' });

    let completed = 0;
    let failed = 0;
    const errors: string[] = [];

    for (let i = 0; i < spendingDemandSelected.length; i++) {
      const spendingDemand = spendingDemandSelected[i];
      setGroupedActionsProgress(prev => ({ ...prev, current: i + 1 }));

      try {
        let result;
        switch (action) {
          case 'VALIDATE':
            result = await SpendingDemandAPI.validateSpendingDemandAPIProtected(
              spendingDemand.spending_demand_id,
              message || ""
            );
            break;
          case 'REJECT':
            result = await SpendingDemandAPI.refuseSpendingDemandAPIProtected(
              spendingDemand.spending_demand_id,
              message || ""
            );
            break;
          case 'REQUEST_CORRECTION':
            result = await SpendingDemandAPI.askForUpdateSpendingDemandAPIProtected(
              spendingDemand.spending_demand_id,
              message || ""
            );
            break;
        }

        console.log('result', result);
        completed++;
        setGroupedActionsProgress(prev => ({ ...prev, completed })); //eslint-disable-line
        
        // Invalider les queries pour cette demande spécifique
        queryClient.invalidateQueries({
          queryKey: ["spendingdemand", spendingDemand.spending_demand_id, "historic_status"],
        });

      } catch (error) {
        failed++;
        errors.push(`Demande ${spendingDemand.spending_demand_id}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
        setGroupedActionsProgress(prev => ({ ...prev, failed })); //eslint-disable-line
      }
    }

    setIsLoadingActionGrouped(false);
    
    // Invalider les queries générales
    queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
    queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
    queryClient.invalidateQueries({ queryKey: ["teams"] });

    // Afficher le message de résultat
    if (failed === 0) {
      const successMessage = `Toutes les demandes ont été traitées avec succès (${completed}/${spendingDemandSelected.length})`;
      console.log('Setting success message:', successMessage);
      setGroupedActionsMessage({
        type: 'success',
        message: successMessage
      });
    } else if (completed === 0) {
      const errorMessage = `Toutes les demandes ont échoué (${failed}/${spendingDemandSelected.length}). Erreurs: ${errors.join(', ')}`;
      console.log('Setting error message:', errorMessage);
      setGroupedActionsMessage({
        type: 'error',
        message: errorMessage
      });
    } else {
      const partialMessage = `Traitement partiel: ${completed} succès, ${failed} échecs. Erreurs: ${errors.join(', ')}`;
      console.log('Setting partial message:', partialMessage);
      setGroupedActionsMessage({
        type: 'error',
        message: partialMessage
      });
    }

    // Réinitialiser la sélection après 3 secondes pour laisser le temps de lire le message
    setTimeout(() => {
      setSpendingDemandSelectedList([]);
      setGroupedActionsMessage({ type: null, message: '' });
      setGroupedActionsProgress({ current: 0, total: 0, completed: 0, failed: 0 });
    }, 3000);
  };

  const validateGroupedSpendingDemand = (
    spendingDemandSelected: SpendingDemand[] | null, message?: string
  ) => {
    processGroupedActionsSequentially(spendingDemandSelected, 'VALIDATE', message);
  };

  const rejectGroupedSpendingDemand = (
    spendingDemandSelected: SpendingDemand[] | null, message?: string
  ) => {
    processGroupedActionsSequentially(spendingDemandSelected, 'REJECT', message);
  };

  const requestCorrectionGroupedSpendingDemand = (
    spendingDemandSelected: SpendingDemand[] | null, message?: string
  ) => {
    processGroupedActionsSequentially(spendingDemandSelected, 'REQUEST_CORRECTION', message);
  };

  const groupedActions = (action: Action, message?: string) => {
    switch (action) {
      case "VALIDATE":
        validateGroupedSpendingDemand(spendingDemandSelected, message);
        break;
      case "REJECT":
        rejectGroupedSpendingDemand(spendingDemandSelected, message);
        break;
      case "REQUEST_CORRECTION":
        requestCorrectionGroupedSpendingDemand(spendingDemandSelected, message);
        break;
    }
  };

  const setSelectMode = (selectMode: boolean) => {
    dispatch(setSelectModeAction(selectMode));
  };

  const isSpendingDemandeSelected = (spendingDemandId: string) => {
    return spendingDemandSelected?.some(
      (spendingDemand) => spendingDemand.spending_demand_id === spendingDemandId
    );
  };

  const selectSpendingDemand = (spendingDemand: SpendingDemand) => {
    if (!spendingDemandSelected){
      setSpendingDemandSelectedList([spendingDemand])
      return
    }else{
      setSpendingDemandSelectedList([ ...spendingDemandSelected, spendingDemand])
    }
  }

  const unSelectSpendingDemand = (spendingDemand: SpendingDemand) => {
    if (!spendingDemandSelected) return
    setSpendingDemandSelectedList(spendingDemandSelected.filter((spending) => spending.spending_demand_id !== spendingDemand.spending_demand_id))
  }

  const selectAllSpendingDemands = (allSpendingDemands: SpendingDemand[] | SpendingDemandExtended[]) => {
    const currentPageDemands = allSpendingDemands as SpendingDemand[];
    
    if (!spendingDemandSelected) {
      setSpendingDemandSelectedList(currentPageDemands);
      return;
    }
    
    // Ajouter les éléments de la page courante qui ne sont pas déjà sélectionnés
    const newSelections = currentPageDemands.filter(pageDemand => 
      !spendingDemandSelected.some(selected => selected.spending_demand_id === pageDemand.spending_demand_id)
    );
    
    setSpendingDemandSelectedList([...spendingDemandSelected, ...newSelections]);
  }

  const unSelectAllSpendingDemands = (currentPageDemands?: SpendingDemand[] | SpendingDemandExtended[]) => {
    if (!spendingDemandSelected) return;
    
    if (!currentPageDemands) {
      // Si aucune page spécifiée, désélectionner tout
      setSpendingDemandSelectedList([]);
      return;
    }
    
    // Désélectionner seulement les éléments de la page courante
    const pageDemandIds = (currentPageDemands as SpendingDemand[]).map(demand => demand.spending_demand_id);
    const remainingSelections = spendingDemandSelected.filter(selected => 
      !pageDemandIds.includes(selected.spending_demand_id)
    );
    
    setSpendingDemandSelectedList(remainingSelections);
  }

  const isAllSpendingDemandsSelected = (allSpendingDemands: SpendingDemand[] | SpendingDemandExtended[]) => {
    if (!spendingDemandSelected || spendingDemandSelected.length === 0) return false
    if (!allSpendingDemands || allSpendingDemands.length === 0) return false
    
    // Vérifier si tous les éléments de la page courante sont sélectionnés
    return (allSpendingDemands as SpendingDemand[]).every(spendingDemand => 
      spendingDemandSelected.some(selected => selected.spending_demand_id === spendingDemand.spending_demand_id)
    )
  }

  const isSomeSpendingDemandsSelected = (allSpendingDemands: SpendingDemand[] | SpendingDemandExtended[]) => {
    if (!spendingDemandSelected || spendingDemandSelected.length === 0) return false
    if (!allSpendingDemands || allSpendingDemands.length === 0) return false
    
    // Vérifier si au moins un élément de la page courante est sélectionné
    return (allSpendingDemands as SpendingDemand[]).some(spendingDemand => 
      spendingDemandSelected.some(selected => selected.spending_demand_id === spendingDemand.spending_demand_id)
    )
  }

  const createNewSpendingDemand = useMutation({
    mutationFn: (input:{spendingDemand: Partial<SpendingDemand>, files?: FileUrl[]}, callback?: CallableFunction) => {
      return SpendingDemandAPI.newDemandSpendingAPI(input.spendingDemand).then((data) => {

        if (input.files && data?.spending_demand_id && data.spending_demand_id !== "") {
          input.files.forEach(async (file) => {
            try{
              await SpendingDemandAPI.addNewFileToSpendingDemandAPI(data.spending_demand_id, file)
            }catch(e){
              console.error("error adding file to spending demand", e)
            }
          })
        }

        if (callback) {
          callback({result:data});
        }


        return Promise.resolve(data);
      }).catch((error) => {
        if (callback) {
          callback({error});
        }
        return Promise.reject(error);
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
    },
    onError: (error) => {
      handleInternError(error.message,null);
    },

  })

  const setPayedSingleSpending = useMutation<any,any, {spendingDemandId:string, callback?: (c:{result?: Spending, err?: any}) => void }>({
    mutationFn: async ({spendingDemandId, callback} ) => {
        try{
        const spendingCreadtedFromSpendingDemand = await  SpendingDemandAPI.setPayed(spendingDemandId)
        if(callback){
          callback({result: spendingCreadtedFromSpendingDemand})
        }
        return spendingCreadtedFromSpendingDemand
        }catch(e){
          const error = e as AxiosError
          handleNetworkError(error, "allSpendingDemandListQuery")
          if(callback){
            callback({err: error})
          }
          throw e
        }
    },
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['spendingdemand']})
        queryClient.invalidateQueries({ queryKey: ['teams']})
        queryClient.invalidateQueries({ queryKey: ['spending']})
    }
})

  // Extract functions
  const startExtract = useMutation({
    mutationFn: async () => {
      setIsExtracting(true);
      setExtractStatus({ status: null, total: 0, actual: 0 });
      
      const { queryArray } = FilterpendingDemandConverterToQuery(filter);
      const whereQueryParam = queryArray.find(q => q.key === 'whereQuery')?.value ?? undefined;
      const orderByParam = queryArray.find(q => q.key === 'orderBy')?.value ?? undefined;
      
      try {
        const result = forUser
          ? await SpendingDemandAPI.startExtractSpendingDemandsUserAPI(
              whereQueryParam,
              orderByParam,
              selectedExtractFields
            )
          : await SpendingDemandAPI.startExtractSpendingDemandsAPI(
              whereQueryParam,
              orderByParam,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              userId,
              selectedExtractFields
            );
        
        setExtractProcessId(result.processID);
        return result;
      } catch (error) {
        setIsExtracting(false);
        throw error;
      }
    },
    onError: (error) => {
      setIsExtracting(false);
      handleNetworkErrorFunction && handleNetworkErrorFunction(error as AxiosError, "extract");
    }
  });

  const checkExtractStatus = useMutation({
    mutationFn: async (processID: string) => {
      return await SpendingDemandAPI.getExtractStatusAPI(processID);
    },
    onSuccess: (data) => {
      setExtractStatus({
        status: data.status as 'PENDING' | 'COMPLETED' | 'ERROR' | 'NOT_FOUND',
        total: data.total,
        actual: data.actual
      });
      if (data.status === 'COMPLETED' || data.status === 'ERROR') {
        setIsExtracting(false);
      }
    },
    onError: (error) => {
      setIsExtracting(false);
      setExtractStatus({ status: 'ERROR', total: 0, actual: 0 });
      handleNetworkErrorFunction && handleNetworkErrorFunction(error as AxiosError, "extractStatus");
    }
  });

  const downloadExtract = useMutation({
    mutationFn: async (processID: string) => {
      const response = await SpendingDemandAPI.downloadExtractAPI(processID);
      
      // Créer un blob et télécharger le fichier
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `spending_demands_extract_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return response;
    },
    onSuccess: () => {
      // Réinitialiser l'état après téléchargement
      setExtractProcessId(null);
      setExtractStatus({ status: null, total: 0, actual: 0 });
    },
    onError: (error) => {
      handleNetworkErrorFunction && handleNetworkErrorFunction(error as AxiosError, "downloadExtract");
    }
  });

  // Polling pour vérifier le statut de l'extraction
  useEffect(() => {
    if (!extractProcessId || !isExtracting) return;

    const interval = setInterval(() => {
      checkExtractStatus.mutate(extractProcessId);
    }, 2000); // Vérifier toutes les 2 secondes

    return () => clearInterval(interval);
  }, [extractProcessId, isExtracting, checkExtractStatus]);

  
  return {
    allSpendingDemandList: {
      data: allSpendingDemandListQuery.data,
      isLoading: allSpendingDemandListQuery.isLoading,
      isError: allSpendingDemandListQuery.isError,
      error: allSpendingDemandListQuery.error,
      isFetching: allSpendingDemandListQuery.isFetching,
      isSuccess: allSpendingDemandListQuery.isSuccess,
    },

    setPayedSingleSpending,
    setSpendingDemandSelectedList,
    spendingDemandSelected,
    groupedActions,
    isLoadingActionGrouped,
    groupedActionsProgress,
    groupedActionsMessage,
    setSelectMode,
    isSelectMode,
    isSpendingDemandeSelected,
    selectSpendingDemand,
    unSelectSpendingDemand,
    selectAllSpendingDemands,
    unSelectAllSpendingDemands,
    isAllSpendingDemandsSelected,
    isSomeSpendingDemandsSelected,
    createNewSpendingDemand,
    
    // Extract functions
    startExtract,
    checkExtractStatus,
    downloadExtract,
    extractProcessId,
    extractStatus,
    isExtracting,
    selectedExtractFields,
    setSelectedExtractFields
  };
};

export default useSpendingDemandCore;
